/**
 * A caixa de vencimentos que abre uma vez, quando a página termina de carregar.
 *
 * Papel: espera `useItensVencendo` responder e, se a lista tiver algum item,
 * abre o `PopupVencimentos` com ela; fechada a caixa — pelo "Fechar", pelo
 * `Esc`, pelo véu ou pelo "Ver em Documentos & segurança" —, ela não volta a
 * abrir sozinha até a página ser carregada de novo. A abertura pelo sino da
 * barra superior é `abertaPeloBotao`, guardada por quem monta, e não passa por
 * esta regra.
 *
 * Como usar: o único lugar que monta este componente é o `Layout`, que fica
 * montado durante toda a navegação de uma tela para outra. É isso que faz o
 * estado `etapa` durar o carregamento inteiro: montado dentro de uma página,
 * o componente nasceria de novo a cada visita a ela, e a caixa reapareceria
 * toda vez que alguém voltasse à tela inicial.
 *
 * A decisão de abrir acontece uma vez só, no primeiro momento em que
 * `respondido` fica verdadeiro, e vale o que a lista tiver naquele instante.
 * Uma lista vazia encerra a caixa sem abri-la, e um documento gravado depois
 * disso com a validade dentro da janela aparece só no sino — a caixa é o
 * anúncio da abertura, e não um alarme que dispara a cada gravação.
 *
 * Quem pode ver algum dos módulos do aviso mas não `documentos` vê a caixa só
 * com o "Fechar": o botão que leva a `/documentos` terminaria numa tela
 * recusada pela API.
 */
import { ROTULOS_MODULO } from '@concierge/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { PopupVencimentos } from '@/shared/ui/popup-vencimentos';
import { useItensVencendo } from './use-itens-vencendo';

interface PopupVencimentosDaAberturaProps {
  /** Verdadeiro enquanto o sino da barra estiver com a caixa aberta por clique. */
  abertaPeloBotao: boolean;
  /** Chamado quando a caixa é fechada, para que quem monta zere `abertaPeloBotao`. */
  onFecharPeloBotao: () => void;
}

/** Em que ponto a caixa está: esperando as consultas, aberta ou já encerrada. */
type Etapa = 'esperando' | 'aberta' | 'encerrada';

/**
 * A caixa de vencimentos da abertura da página, também aberta pelo sino.
 *
 * @param abertaPeloBotao - Se a caixa deve aparecer por causa do clique no
 *   sino, independente da abertura automática.
 * @param onFecharPeloBotao - Chamado em qualquer fechamento, para zerar o
 *   estado de quem monta.
 * @returns O `PopupVencimentos`, aberto na abertura automática ou pelo sino.
 */
export function PopupVencimentosDaAbertura({
  abertaPeloBotao,
  onFecharPeloBotao,
}: PopupVencimentosDaAberturaProps) {
  const { itens, respondido, podeVerDocumentos } = useItensVencendo();
  const navegar = useNavigate();
  const [etapa, setEtapa] = useState<Etapa>('esperando');

  // A decisão é tomada durante a renderização, e não num efeito: assim a caixa
  // já sai aberta na mesma passada em que as consultas respondem, sem um
  // quadro intermediário com a tela ainda livre. A condição sobre `etapa` é o
  // que impede a troca de se repetir.
  if (etapa === 'esperando' && respondido) {
    setEtapa(itens.length > 0 ? 'aberta' : 'encerrada');
  }

  /** Encerra a caixa sem sair da tela em que se está. */
  function handleFechar() {
    setEtapa('encerrada');
    onFecharPeloBotao();
  }

  /** Encerra a caixa e leva à tela de documentos. */
  function handleVerDocumentos() {
    setEtapa('encerrada');
    onFecharPeloBotao();
    void navegar('/documentos');
  }

  return (
    <PopupVencimentos
      itens={itens}
      aberto={etapa === 'aberta' || abertaPeloBotao}
      onFechar={handleFechar}
      rotuloAcao={podeVerDocumentos ? `Ver em ${ROTULOS_MODULO.documentos}` : undefined}
      onAcao={podeVerDocumentos ? handleVerDocumentos : undefined}
    />
  );
}
